import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import uniqueId from 'lodash/uniqueId';
import { Flyout } from '@els/els-react--flyout';
import AvailabilityProviders from '@Components/Results/structure/availability/AvailabilityProviders';
import { getShoppingCartIcon, loadShoppingCartImage } from '@Components/Results/structure/availability/ShoppingCartHelper';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import HoverTooltip from '@Components/HoverTooltip';
import { TooltipConstants } from '@Components/Tooltip';
import ReactDOMServer from 'react-dom/server';
import ShoppingCartTooltipContent from '@Components/Results/structure/availability/ShoppingCartTooltipContent';
import Catalyst from '@Services/Catalyst';
import { Badge } from '@els/els-react--badge';
import { ButtonWithIcon } from '@els/els-react--button';
import NumberFormatter from '@Utils/NumberFormatter';

import './ShoppingCartContent.scss';

function ShoppingCartContent({
  tooltipId,
  supplierNumber,
  structure,
  trackingContext,
  onOptionClick = () => null,
  availability,
  onOpen = () => null,
  trackingContent,
}) {
  const [isOpen, setIsOpen] = useState(false);

  const shippingDescId = useMemo(() => uniqueId('shopping-cart-shipping-desc-'), []);

  const toggleMenu = (isOpenValue) => {
    const open = typeof isOpenValue === 'boolean' ? isOpenValue : isOpenValue?.isOpen;
    setIsOpen(open);
    if (open) {
      const tooltip = document.getElementById(TooltipConstants.ids.GlobalHover);
      if (tooltip) {
        tooltip.dispatchEvent(
          new MouseEvent('mouseleave', {
            bubbles: true,
          })
        );
      }
      onOpen();
      Catalyst.trackEvent('buttonClick', {
        buttonType: 'menu:shopping cart',
        content: trackingContent,
      });
    }
  };

  const details = structure.details || {};
  const tooltipLabel = 'Commercial Suppliers';
  const shoppingCartTooltipLabel = 'Number of Suppliers';

  return (
    <div className="shopping-cart-block">
      <span id={shippingDescId} className="sr-only">
        Shipping time: {details.shippingTime}, Best Price: {details.bestPrice}, Largest available package size: {details.maxPackSize}
      </span>
      <Flyout
        className="structure-toolbar-options"
        isControlled
        isOpen={isOpen}
        flyout={
          <div className="u-els-nowrap" data-testid="shopping-cart-flyout">
            <div className="u-els-display-flex u-els-margin-bottom-1o2 u-els-margin-right-2x u-els-font-size-h4">
              Substance Availability
            </div>
            <AvailabilityProviders
              closeMenu={() => {}}
              providers={availability.providers}
              loadImage={loadShoppingCartImage}
              structure={structure}
              trackingContext={trackingContext}
              onProviderClick={onOptionClick}
              isOpen={isOpen}
              details={details}
            />
          </div>
        }
        shouldCloseOnESC
        showCloseButton
        onToggle={toggleMenu}
        placement="bottom-start"
        pointerType="primary">
        <HoverTooltip
          id={TooltipConstants.ids.GlobalHover}
          tooltip={ReactDOMServer.renderToString(
            <ShoppingCartTooltipContent details={details} supplierNumber={supplierNumber} className="shopping-cart-tooltip__content" />
          )}
          tooltipPlace="top"
          theme="simple">
          <ButtonWithIcon
            sprite={getShoppingCartIcon(details)}
            onClick={() => {}}
            type="borderless"
            iconOnly
            aria-expanded={isOpen}
            data-e2e="shopping-cart-button"
            aria-label={getShoppingCartIcon(details) === 'RapidDelivery' ? `Rapid Substance availability` : `Substance availability`}
            aria-describedby={shippingDescId}>
            {tooltipId ? '' : tooltipLabel}
          </ButtonWithIcon>
        </HoverTooltip>
      </Flyout>
      {Auth.isFeatureEnabled(FEATURES.enableDisplaySupplierNumber) && !!supplierNumber && (
        <Badge
          type="subtle"
          size="base"
          label={shoppingCartTooltipLabel}
          className="supplier-number-badge"
          content={NumberFormatter(supplierNumber)}
          overlap="space-1o2"
        />
      )}
    </div>
  );
}

ShoppingCartContent.propTypes = {
  availability: PropTypes.object.isRequired,
  structure: PropTypes.object.isRequired,
  trackingContext: PropTypes.string,
  onOpen: PropTypes.func,
  onOptionClick: PropTypes.func,
  supplierNumber: PropTypes.number,
  tooltipId: PropTypes.string,
  trackingContent: PropTypes.array,
};

export default ShoppingCartContent;
